import React from 'react'
import { DelayedTextInput } from '../form/textInput'
import { Popover } from '../../layouts/components/popover'
import { capitalize, compareNames, mapWithDivider, unwrapSetStateAction } from '../../utils'
import { showAxiosErrorAsToast, useRequest } from '../../request'
import { DropdownDivider, dropdownItemStyle } from '../simple/dropdown'
import { useTranslation } from '../../providers/translation'
import { IconAngleDown, IconAngleRight, IconTimes } from '../../icons/library'
import { ButtonStyle } from '../simple/button'
import { Toasts } from '../../providers/toast'
import {
  BasicUserOrGroup,
  mergeUsers,
  UserBadge,
  UserDropdownItem,
  UserDropdownInfo,
  PermenantUserBadge,
  preferExistingUser,
} from './users'
import assert from 'assert'

const MAX_PASTE_RECIPIENTS = 100
interface UserSelectorProps<RT> {
  /** The term used to describe the users. */
  userDescriptor?: string
  /** The users that are currently selected. */
  selectedUsers: RT[]
  /** The users that are already loaded and should always be available. */
  loadedUsers?: RT[]
  /** Names to always show as 'selected' in the selector, unconnected to the actual selected users. */
  extraNames?: string[]
  /** A function to call when the selected users change. */
  setSelectedUsers: React.Dispatch<React.SetStateAction<RT[]>>
  /** Whether the input should have a clear button when there are selected users or a non-empty search. */
  clearable?: boolean
  /** Whether to limit to max one single user. */
  limitUsers?: boolean
  /** What to show in the search input as placeholder. */
  placeholder?: string
  /** Data to pass along with the search when loading users from `url` */
  searchData?: Record<string, any>
  /** Additional classes for styling the user selector. */
  className?: string
  /** The URL to fetch users from. */
  url: string
  /** Whether the user selector is disabled and should not be usable. */
  disabled?: boolean
  /** Whether the user selector is invalid and should be highlighted as such. */
  invalid?: boolean
  /** A function to limit visible users based on their properties. */
  userVisibleFilter?: (user: RT) => boolean
  /** A function to check if each user should be disabled and/or have a tooltip when rendered. */
  userDisplayConfig?: (user: RT) => { disabled?: boolean; tooltip?: string } | undefined
  /** An action to load corresponding users when a list of identifiers is pasted into the input. */
  fetchUsersByIdentifiersForPaste?: (identifiers: string[]) => Promise<RT[]>
  /** Controls the double click action on a user. */
  doubleClickAction?: {
    /** A function to check if the user can be double clicked and what to show in the tooltip if they can be. */
    canDoubleClick: (user: RT) => { tooltip: string } | undefined
    /** A function to call when the user is double clicked, which should return the new users to select. */
    onDoubleClick: (user: RT) => Promise<RT[]>
  }
  /** Custom sorting for the user list that overrides the default sorting. */
  customSort?: (a: RT, b: RT) => number
  /** Extra custom sections to show in the dropdown. These are basically plugins for the user selector. */
  customSections?: {
    title: string
    body: (closeSection: () => void) => React.ReactElement
  }[]
  /** An ID for the input element. */
  id?: string
  /** Whether to hide the input when limiting users and the input is full. */
  hideInputWhenFull?: boolean
  /** Whether to always show the preloaded users before the remote users in the user list. */
  loadedUsersFirst?: boolean
  /** Whether to show the users' default tooltips when hovering over the badges. */
  showTooltipOnBadges?: boolean
  /** A value and setter for the search input to be controlled externally */
  controlSearch?: readonly [string, (search: string) => void]
  /** Whether to avoid showing unnecessary messages in the user selector. */
  hideUnnecessaryMessages?: boolean
  /** Whether to continue showing the last search results when the search is cleared */
  preserveResultsOnEmptySearch?: boolean
  /** Whether to hide already selected users in the dropdown list. */
  hideSelectedUsersInDropdown?: boolean
  /** Whether to avoid wrapping the input and badges in the selector. */
  noWrapping?: boolean
  /** Whether to show user type indicators after each name in the dropdown (e.g., student, teacher, etc.). */
  showTypeIndicators?: boolean
}

/**
 * A component that allows the user to select users from a list. This component handles searching, filtering, and displaying users.
 * It also allows for custom sections and double click actions on users.
 */
export const UserSelector = <RT extends BasicUserOrGroup>({
  selectedUsers,
  setSelectedUsers,
  extraNames,
  clearable,
  limitUsers,
  placeholder,
  loadedUsers = [],
  searchData,
  className,
  url,
  disabled,
  userVisibleFilter = () => true,
  userDisplayConfig = () => undefined,
  fetchUsersByIdentifiersForPaste,
  doubleClickAction,
  customSections = [],
  customSort,
  userDescriptor = 'users',
  invalid,
  id,
  hideInputWhenFull,
  loadedUsersFirst = true,
  showTooltipOnBadges,
  controlSearch,
  hideUnnecessaryMessages,
  preserveResultsOnEmptySearch = true,
  hideSelectedUsersInDropdown,
  noWrapping,
  showTypeIndicators,
}: UserSelectorProps<RT>) => {
  const [search, setSearch] = controlSearch ?? React.useState<string>('')

  const [keyboardSelectedUser, setKeyboardSelectedUser] = React.useState<RT | undefined>(undefined)
  const [open, setOpen] = React.useState<boolean>(false)
  const [loadingAction, setLoadingAction] = React.useState<boolean>(false)

  const [openSections, setOpenSections] = React.useState<number[]>([])
  const inputRef = React.useRef<HTMLInputElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const { tr } = useTranslation()

  const postData = React.useMemo(() => {
    setKeyboardSelectedUser(undefined)
    return {
      search: search.trim(),
      ...searchData,
    }
  }, [search, searchData])

  const { data: remoteUsers, loading: loadingUsers } = useRequest<RT[]>(
    url || '',
    postData,
    search.trim() !== '' && url !== undefined
  )
  const loading = loadingAction || loadingUsers
  const allUsers = React.useMemo(() => {
    const users = mergeUsers(mergeUsers(loadedUsers, selectedUsers), remoteUsers || [])
    // check some common data loadimg issues here so we can catch them early
    assert(!users.some((r) => r === undefined || typeof r !== 'object'), 'UserSelector: undefined user in users')
    return users
  }, [loadedUsers, selectedUsers, remoteUsers])
  const users = React.useMemo(() => {
    return allUsers
      .filter((r) => {
        if (search.trim() === '' && !preserveResultsOnEmptySearch) return false
        if (hideSelectedUsersInDropdown && selectedUsers.includes(r)) return false
        if (!userVisibleFilter(r)) return false
        if (r.group && limitUsers) return false
        if (search.trim() === '') return true
        if (r.name?.toLowerCase().includes(search.trim().toLowerCase())) return true
        return !!r.exactMatchSearchVals?.some((val) => val && val.toLowerCase() === search.trim().toLowerCase())
      })
      .sort((a, b) => {
        if (customSort) {
          const sortValue = customSort(a, b)
          if (sortValue !== 0) return sortValue
        }
        if ((a.sortPriority || 0) !== (b.sortPriority || 0)) return (b.sortPriority || 0) - (a.sortPriority || 0)
        if (a.group !== b.group) return a.group ? -1 : 1
        if (loadedUsersFirst) {
          const aLoaded = loadedUsers.includes(a)
          const bLoaded = loadedUsers.includes(b)
          if (aLoaded !== bLoaded) return aLoaded ? -1 : 1
        }
        return compareNames(a.name, b.name)
      })
  }, [allUsers, selectedUsers, userVisibleFilter, limitUsers, customSort, search.trim() === ''])
  // ^^^ intentionally dont update on search (wait until remote data is loaded). Just update on search exists or not, becuase we keep the previous remote data when search is empty

  React.useEffect(() => {
    if (selectedUsers.some((r) => userDisplayConfig(r)?.disabled || userVisibleFilter(r) !== true))
      setSelectedUsers((sr) => sr.filter((r) => !userDisplayConfig(r)?.disabled && userVisibleFilter(r) === true))
  }, [userDisplayConfig, userVisibleFilter])

  React.useEffect(() => {
    if (limitUsers && (selectedUsers.length > 1 || selectedUsers[0]?.group)) {
      const singleUsers = selectedUsers.filter((r) => !r.group)
      setSelectedUsers(singleUsers.length > 0 ? [singleUsers[0]] : [])
    }
  }, [limitUsers])

  const selectedThroughGroupIDs = React.useMemo(
    () => new Set<string | number>(selectedUsers.flatMap((r) => (r.group ? r.memberIDs || [] : []))),
    [selectedUsers]
  )

  const toggleUser = (user: RT) => {
    if (user.notToggleable) return
    if (selectedUsers.includes(user)) {
      setSelectedUsers((sr) => sr.filter((r) => r !== user))
    } else {
      if ((!user.group && selectedThroughGroupIDs.has(user.id)) || userDisplayConfig(user)?.disabled) return
      const userIDsToRemove = new Set<string | number>(user.group ? user.memberIDs : [])
      setSelectedUsers((sr) => [...(limitUsers ? [] : sr.filter((r) => r.group || !userIDsToRemove?.has(r.id))), user])
      setSearch('')
    }
    inputRef.current?.focus()
  }

  const navigateKeyboardUsers = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }

    if (e.key === 'Backspace' && inputRef.current?.value === '' && selectedUsers.length > 0) {
      // we use inputRef.current?.value instead of search because the search state is delayed (see DelayedInput)
      setSelectedUsers((sr) => sr.slice(0, -1))
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const index = users.findIndex((r) => r === keyboardSelectedUser)
      if (index === -1) {
        if (e.key === 'ArrowUp') setKeyboardSelectedUser(users[users.length - 1])
        else setKeyboardSelectedUser(users[0])
      } else {
        const newIndex = (e.key === 'ArrowUp' ? index + users.length - 1 : index + 1) % users.length
        setKeyboardSelectedUser(users[newIndex])
      }
    }

    if (e.key === 'Enter' && keyboardSelectedUser) toggleUser(keyboardSelectedUser)
    else if (e.key === 'Enter' && users.length === 1) {
      toggleUser(users[0])
      setSearch('')
    }
  }

  // this animation allows it to not instantly hide, which prevents flickering when clicking on the input
  const anim = 'data-[state=closed]:animate-delayedHide'

  const invalidClass = `transition-colors duration-150 ${invalid ? 'border-red-500' : ''}`

  const nothingToShow = users.length === 0 && hideUnnecessaryMessages

  return (
    <Popover
      aggressivelyHide={hideUnnecessaryMessages}
      onOpenAutoFocus={(e) => e.preventDefault()} // dont take focus away from the input
      onCloseAutoFocus={(e) => e.preventDefault()}
      open={open && !nothingToShow}
      onKeyDown={navigateKeyboardUsers}
      onOpenChange={(open) => {
        setTimeout(() => {
          // timeout is to wait for the input to unfocus first if the click was outside the input
          const inputFocus = !!inputRef.current && inputRef.current?.contains(document.activeElement)
          const reallyOpen = open || inputFocus
          setOpen(reallyOpen)
          if (!reallyOpen) inputRef.current?.blur()
          else inputRef.current?.focus()
        })

        if (!open) setOpenSections([])
      }}
      align="start"
      trigger={
        <div
          id={id}
          className={`relative flex min-h-[33.5px] ${noWrapping ? '' : 'max-w-[850px] flex-wrap'} gap-1 rounded-[16.8px] border border-gray-200 p-1 ${open ? 'ring-2 ring-gray-200 ring-opacity-50' : ''} ${disabled ? 'pointer-events-none opacity-50' : ''} ${className} ${invalidClass}`}
          ref={wrapperRef}
        >
          {(extraNames ?? []).map((user, i) => (
            <PermenantUserBadge key={i}>{user}</PermenantUserBadge>
          ))}
          {selectedUsers.map((user) => (
            <UserBadge
              key={user.group ? 'G=' + user.groupID : user.id}
              user={user}
              onClick={() => toggleUser(user)}
              showDefaultTooltip={showTooltipOnBadges}
            />
          ))}
          {!(hideInputWhenFull && limitUsers && selectedUsers.length === 1) && (
            <DelayedTextInput
              disabled={loadingAction}
              ref={inputRef}
              nooutline
              value={search}
              onChange={setSearch}
              growToContent={noWrapping}
              placeholder={placeholder ?? capitalize(userDescriptor)}
              className="height-[24.49px] ml-1 flex-grow placeholder:text-[13px]"
              onKeyDown={navigateKeyboardUsers}
              onFocusChange={(focus) => {
                if (focus) setTimeout(() => setOpen(true))
              }}
              onPaste={
                fetchUsersByIdentifiersForPaste
                  ? (value: string) => {
                      const identifiers = value
                        .split(/\t|\r?\n|,/)
                        .map((s) => s.trim())
                        .filter((s) => s.length > 0)
                      if (identifiers.length <= 1) return true // allow default paste action
                      if (identifiers.length > MAX_PASTE_RECIPIENTS) {
                        identifiers.splice(MAX_PASTE_RECIPIENTS)
                        Toasts.showToast({
                          message: `You can only paste ${MAX_PASTE_RECIPIENTS} users at a time. The rest have been discarded`,
                          error: true,
                          duration: 60000,
                        })
                      }
                      setLoadingAction(true)
                      fetchUsersByIdentifiersForPaste(identifiers)
                        .then((newUsers) => {
                          const usersToSelect = preferExistingUser(allUsers, newUsers)
                          setSelectedUsers((sr) => mergeUsers(sr, usersToSelect))
                        })
                        .catch((error) => showAxiosErrorAsToast(error, 'Error fetching pasted users. Please try again'))
                        .finally(() => setLoadingAction(false))
                      return false // prevent default paste action
                    }
                  : undefined
              }
            />
          )}
          {clearable && (selectedUsers.length > 0 || search.length > 0) && (
            <div
              className={`absolute right-1 top-[.3rem] z-10 ${ButtonStyle.translucentClose}`}
              onClick={() => {
                setSelectedUsers([])
                setSearch('')
              }}
            >
              <IconTimes thicken={40} />
            </div>
          )}
        </div>
      }
      className={`${loading ? '*:pointer-events-none *:opacity-50' : ''} ${anim} max-h-[350px] overflow-y-auto`}
    >
      {search === '' &&
        !limitUsers &&
        customSections.map((section, i) => (
          <React.Fragment key={i}>
            <div
              className={`${dropdownItemStyle} justify-between text-sm font-bold`}
              onClick={() => setOpenSections((os) => (os.includes(i) ? os.filter((o) => o !== i) : [...os, i]))}
            >
              {section.title}
              {openSections.includes(i) ? (
                <IconAngleDown className="text-link" size="xs" thicken={20} />
              ) : (
                <IconAngleRight className="text-link" size="xs" thicken={20} />
              )}
            </div>
            <DropdownDivider />
            {openSections.includes(i) && (
              <>
                {section.body(() => setOpenSections((os) => os.filter((o) => o !== i)))}
                <DropdownDivider />
              </>
            )}
          </React.Fragment>
        ))}
      {users.length > 0 ? (
        mapWithDivider(
          users,
          (a, b) => a.group !== b.group || a.sortPriority !== b.sortPriority,
          (index) => <DropdownDivider key={`D=${index}`} />,
          (user) => (
            <UserDropdownItem
              key={user.group ? 'G=' + user.groupID : user.id}
              user={user}
              selected={selectedUsers.includes(user)}
              focused={keyboardSelectedUser === user}
              greyedOut={
                (!user.group && selectedThroughGroupIDs.has(user.id)) || userDisplayConfig(user)?.disabled || false
              }
              onClick={() => {
                toggleUser(user)
              }}
              showTypeIndicator={showTypeIndicators}
              tooltipOverride={doubleClickAction?.canDoubleClick(user)?.tooltip ?? userDisplayConfig(user)?.tooltip}
              doubleClickAction={
                doubleClickAction?.canDoubleClick(user)
                  ? () => {
                      setLoadingAction(true)
                      doubleClickAction
                        .onDoubleClick(user)
                        .then((newUsers) => {
                          setSelectedUsers((sr) =>
                            mergeUsers(
                              sr.filter((r) => r !== user),
                              preferExistingUser(allUsers, newUsers)
                            )
                          )
                        })
                        .catch((error) => showAxiosErrorAsToast(error))
                        .finally(() => {
                          setLoadingAction(false)
                        })
                    }
                  : undefined
              }
            />
          )
        )
      ) : search.trim() === '' ? (
        <UserDropdownInfo>{tr(`${userDescriptor} will appear as you start typing`)}</UserDropdownInfo>
      ) : loading ? (
        <UserDropdownInfo>{tr('loading...')}</UserDropdownInfo>
      ) : (
        <UserDropdownInfo>{tr(`no ${userDescriptor} found`)}</UserDropdownInfo>
      )}
    </Popover>
  )
}
type SingleUserSelectorProps<RT> = Omit<UserSelectorProps<RT>, 'limitUsers' | 'selectedUsers' | 'setSelectedUsers'> & {
  /** The currently selected user. */
  selectedUser: RT | undefined
  /** A function to call when the selected user changes. */
  setSelectedUser: (user: RT | undefined) => void
}

/**
 * A wrapper around the UserSelector that limits the selection to a single user.
 * This is useful for cases where you only want to select one user at a time.
 */
export const SingleUserSelector = <RT extends BasicUserOrGroup>({
  selectedUser,
  setSelectedUser,
  ...props
}: SingleUserSelectorProps<RT>) => {
  const users = React.useMemo(() => (selectedUser ? [selectedUser] : []), [selectedUser])
  return (
    <UserSelector
      limitUsers
      hideInputWhenFull
      selectedUsers={users}
      setSelectedUsers={unwrapSetStateAction(users, (ts) => setSelectedUser(ts[0]))}
      {...props}
    />
  )
}
